import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { ENQUIRY_STAGES_BY_KEY, SUB_STATUSES, SUB_STATUSES_BY_KEY, BLOCKING_SUB_STATUSES } from "@/lib/workflow";
import {
  ChevronLeft, Loader2, Phone, Mail, MapPin, Clock, Paperclip, Upload,
  UserCircle2, ArrowRightCircle, XCircle, RotateCw, FileText, PenTool,
  Send, CheckCircle2, AlertTriangle, Pause, Pencil, Trash2, Folder,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_LABELS = ENQUIRY_STAGES_BY_KEY;
const FIELD = "w-full bg-white border border-slate-300 rounded-sm p-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none";

export default function EnquiryDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [e, setE] = useState(null);
  const [error, setError] = useState("");
  const [salesList, setSalesList] = useState([]);
  const [coordList, setCoordList] = useState([]);
  const [dealerList, setDealerList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const confirmDelete = async () => {
    if (!window.confirm(`Delete enquiry "${(e || {}).name || ""}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/enquiry/${e.id}`);
      toast.success("Enquiry deleted");
      navigate("/enquiries");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/enquiry/${id}`);
      setE(data);
    } catch (err) { setError(formatApiError(err)); }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    (async () => {
      try {
        const [s, c, d] = await Promise.all([
          api.get("/enquiry/sales"),
          api.get("/enquiry/coordinators"),
          api.get("/enquiry/dealers"),
        ]);
        setSalesList(s.data); setCoordList(c.data); setDealerList(d.data);
      } catch (_) { /* noop */ }
    })();
  }, []);

  const doAction = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (error) return <DesktopLayout><div className="text-red-600">{error}</div></DesktopLayout>;
  if (!e) return <DesktopLayout><div className="text-slate-500">Loading…</div></DesktopLayout>;

  const badge = STAGE_LABELS[e.stage] || STAGE_LABELS.new;
  const subBadge = e.sub_status && SUB_STATUSES_BY_KEY[e.sub_status];
  const isBlocked = e.sub_status && BLOCKING_SUB_STATUSES.has(e.sub_status);
  const isClosed = ["lost", "converted"].includes(e.stage);

  const role = user?.role;
  const canAssignSales = role === "super_admin" && !isClosed;
  const canAttend = role === "sales" && e.assigned_sales_id === user.id && e.stage === "assigned" && !isBlocked;
  // Sales owner (or Super Admin) can reassign the Sales Coordinator after
  // the enquiry is created — helps when the original coord is on leave or
  // handling too many enquiries. Not available on closed / lost enquiries.
  const canReassignCoord = !isClosed && (
    role === "super_admin" || (role === "sales" && e.assigned_sales_id === user.id)
  );
  // Sales / Sales Coord / Super Admin can attach a dealer to a live enquiry
  // (particularly useful for public enquiries that arrive without a dealer).
  const canAssignDealer = !isClosed && (
    role === "super_admin" || role === "sales_coordination"
    || (role === "sales" && e.assigned_sales_id === user.id)
  );
  // Quote build: Coordination unlocks it once Sales approves the 2D/3D round
  // (stage = quote_prep). Sales (any sales person, not just the owner) can
  // build a quote straight away on a live enquiry — iter100.
  const canCreateQuote =
    (["super_admin", "sales_coordination", "production_coordination"].includes(role)
      && e.stage === "quote_prep" && !isBlocked)
    || (role === "sales" && !isClosed);
  // Dealers skip the whole "wait for Sales Coord to prep" flow — as soon as
  // they've created the enquiry they can build the quote themselves.
  const canDealerCreateQuote = role === "dealer" && e.created_by === user?.id
    && !isClosed && !e.linked_quote_id;
  // iter95 — Push to 2D/3D is available at EVERY live enquiry stage (new →
  // converted); only lost enquiries lock it out. Approve / Reject follow the
  // round state (design_status) rather than the pipeline stage.
  const designStatus = e.design_status;
  const canRequestDrawings = ["super_admin", "sales", "sales_coordination", "production_coordination"].includes(role) && e.stage !== "lost";
  const canMarkDrawingsReady = ["super_admin", "design", "sales_coordination", "production_coordination"].includes(role) && designStatus === "pending";
  // Sales / Coordination approve or reject the submitted round — active only
  // while a round is awaiting a decision.
  const canReviewDrawings = ["super_admin", "sales", "sales_coordination", "production_coordination"].includes(role) && designStatus === "submitted";
  const canSendToSales = ["super_admin", "sales_coordination", "production_coordination"].includes(role) && ["quote_prep"].includes(e.stage) && e.linked_quote_id && !isBlocked;
  const canSendToCustomer = ["super_admin", "sales"].includes(role) && e.stage === "sales_review" && !isBlocked && (role !== "sales" || e.assigned_sales_id === user.id);
  const canMarkApproved = ["super_admin", "sales", "sales_coordination", "production_coordination"].includes(role) && e.stage === "customer_approval" && e.sub_status !== "completed";
  const canConvertToProject = ["super_admin", "sales", "sales_coordination", "production_coordination"].includes(role) && e.stage === "customer_approval" && e.linked_quote_id && !isBlocked;
  const canLose = ["super_admin", "sales", "sales_coordination", "production_coordination"].includes(role) && !isClosed;
  const canReopen = ["super_admin", "sales", "sales_coordination", "production_coordination"].includes(role) && e.stage === "lost";
  const canSetSubStatus = ["super_admin", "sales", "sales_coordination", "production_coordination", "design"].includes(role) && !isClosed;
  const isCreator = e.created_by === user?.id;
  const canEditForm = (isCreator || role === "super_admin" || (role === "sales" && e.assigned_sales_id === user?.id)) && !isClosed;
  const canDelete = (isCreator && ["new", "assigned", "attended"].includes(e.stage)) || role === "super_admin";

  return (
    <DesktopLayout>
      <Link to="/enquiries" className="hk-tiny-label text-orange-600 hover:text-orange-700 inline-flex items-center gap-1">
        <ChevronLeft className="w-3 h-3" /> All enquiries
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mt-2 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <span className="text-[10px] font-black uppercase px-2 py-1 rounded-sm" style={{ color: badge.color, backgroundColor: badge.bg }}>
              {badge.label}
            </span>
            {subBadge && e.sub_status !== "in_progress" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-1 rounded-sm"
                style={{ color: subBadge.color, backgroundColor: subBadge.bg }}
                data-testid="enquiry-sub-status">
                {isBlocked && <AlertTriangle className="w-3 h-3" />}
                {subBadge.label}
              </span>
            )}
            <span className="font-mono text-xs font-bold text-slate-700">{e.enquiry_id}</span>
          </div>
          {e.workflow_status && (
            <div className="text-sm font-bold text-slate-800 mb-1" data-testid="enquiry-workflow-status">
              {e.workflow_status}
            </div>
          )}
          {isBlocked && e.sub_status_reason && (
            <div className="text-xs bg-red-50 border border-red-200 rounded-sm p-2 mb-2 mt-1 inline-block" data-testid="enquiry-blocked-reason">
              <b>{subBadge?.label}:</b> {e.sub_status_reason}
            </div>
          )}
          <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>{e.name}</h1>
          {e.linked_project_id && (
            <div className="mt-1 text-[11px]">
              <Link to={`/projects/${e.linked_project_id}`}
                className="inline-flex items-center gap-1 text-orange-700 hover:text-orange-900 font-bold"
                data-testid="enquiry-project-link">
                → Open project {e.linked_project_pid ? <span className="font-mono">{e.linked_project_pid}</span> : ""}
              </Link>
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {e.phone}</span>
            {e.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {e.email}</span>}
            {e.address && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {e.address}</span>}
            {e.timeline && <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {e.timeline}</span>}
          </div>
        </div>
        {(canEditForm || canDelete) && (
          <div className="flex items-center gap-2">
            {canEditForm && (
              <button onClick={() => setShowEdit(true)} data-testid="enquiry-edit-btn"
                className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-50 text-slate-800 font-black uppercase text-[10px] px-3 py-2 rounded-sm">
                <Pencil className="w-3 h-3" /> Edit
              </button>
            )}
            {canDelete && (
              <button onClick={() => confirmDelete()} data-testid="enquiry-delete-btn"
                className="inline-flex items-center gap-1 border border-red-300 hover:bg-red-50 text-red-700 font-black uppercase text-[10px] px-3 py-2 rounded-sm">
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT — details + files */}
        <div className="lg:col-span-2 space-y-6">
          <section className="hk-card p-5" data-testid="enquiry-details-card">
            <h2 className="hk-tiny-label mb-3">Details</h2>
            <dl className="grid sm:grid-cols-2 gap-4 text-sm">
              <DlPair label="Requirements" value={(e.requirements || []).join(", ") || "—"} />
              <DlPair label="Source" value={e.source === "public" ? "Public form" : "Manual entry"} />
              <DlPair label="Assigned Sales" value={e.assigned_sales_name || "—"} />
              <DlPair label="Assigned Coordinator" value={e.assigned_coordinator_name || "—"} />
              <DlPair label="Assigned Dealer" value={e.assigned_dealer_name || "—"} />
              <DlPair label="Received" value={new Date(e.created_at).toLocaleString()} />
              <DlPair label="Last updated" value={new Date(e.updated_at).toLocaleString()} />
            </dl>
            {e.notes && (
              <div className="mt-4">
                <div className="hk-tiny-label mb-1">Notes</div>
                <div className="text-sm whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-sm p-3">{e.notes}</div>
              </div>
            )}
            {e.lost_reason && (
              <div className="mt-4">
                <div className="hk-tiny-label mb-1">Lost reason</div>
                <div className="text-sm bg-red-50 border border-red-200 rounded-sm p-3">{e.lost_reason}</div>
              </div>
            )}
          </section>

          <section className="hk-card p-5" data-testid="enquiry-files-card">
            <h2 className="hk-tiny-label mb-3">Attachments</h2>
            {(e.files || []).length === 0 ? (
              <div className="text-sm text-slate-500">No files uploaded.</div>
            ) : (
              <FileGroupList enquiryId={e.id}
                              files={e.files}
                              canDelete={["super_admin", "sales_coordination", "production_coordination"].includes(user?.role)}
                              onDeleted={load} />
            )}
            <UploadDrop enquiryId={e.id} onDone={load} />
          </section>

          <section className="hk-card p-5" data-testid="enquiry-timeline-card">
            <h2 className="hk-tiny-label mb-3">Activity</h2>
            <ol className="space-y-3">
              {(e.timeline_log || []).slice().reverse().map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div>
                      <span className="font-bold">{t.actor_name || "System"}</span>
                      <span className="text-slate-500"> · {t.action.replace(/_/g, " ")}</span>
                    </div>
                    {t.note && <div className="text-xs text-slate-500 mt-0.5">{t.note}</div>}
                    {t.reason && <div className="text-xs text-red-700 mt-0.5">Reason: {t.reason}</div>}
                    <div className="text-[11px] text-slate-400 mt-0.5">{new Date(t.at).toLocaleString()}</div>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        {/* RIGHT — actions */}
        <div className="space-y-4">
          {canAssignSales && (
            <ActionCard title="Assign to Sales Rep" icon={UserCircle2}>
              <AssignForm options={salesList} onSubmit={(sales_id) =>
                doAction(() => api.post(`/enquiry/${e.id}/assign-sales`, { sales_id }))} testId="action-assign-sales" cta="Assign" />
            </ActionCard>
          )}
          {canAttend && (
            <ActionCard title="Send to Sales Coordination" icon={ArrowRightCircle}>
              <AttendForm options={coordList} onSubmit={(coordinator_id, call_notes) =>
                doAction(() => api.post(`/enquiry/${e.id}/attend`, { coordinator_id, call_notes }))} />
            </ActionCard>
          )}
          {canReassignCoord && (
            <ActionCard title="Reassign Coordinator" icon={UserCircle2}>
              <AssignForm options={coordList} onSubmit={(coordinator_id) =>
                doAction(() => api.post(`/enquiry/${e.id}/assign-coordinator`, { coordinator_id }))} testId="action-assign-coord" cta="Reassign" />
            </ActionCard>
          )}
          {canAssignDealer && (
            <ActionCard title={e.assigned_dealer_id ? "Reassign Dealer" : "Assign Dealer"} icon={UserCircle2}>
              <p className="text-[11px] text-slate-500 mb-2">
                {e.assigned_dealer_id
                  ? `Currently assigned to ${e.assigned_dealer_name}. Pick a different dealer, or clear the link.`
                  : "Attach this enquiry to a dealer — they will see it in their own list."}
              </p>
              <AssignForm options={dealerList}
                allowClear={!!e.assigned_dealer_id}
                onSubmit={(dealer_id) =>
                  doAction(() => api.post(`/enquiry/${e.id}/assign-dealer`, { dealer_id }))}
                testId="action-assign-dealer"
                cta={e.assigned_dealer_id ? "Reassign" : "Assign"} />
            </ActionCard>
          )}
          {canRequestDrawings && (
            <ActionCard title="Push to 2D/3D Design" icon={PenTool}>
              <RequestDrawingsControl e={e} doAction={doAction} />
            </ActionCard>
          )}
          {canMarkDrawingsReady && (
            <ActionCard title="Mark drawings ready" icon={CheckCircle2}>
              <SimpleNoteForm cta="Drawings ready → Sales approval" testId="action-drawings-ready" optional
                onSubmit={(note) => doAction(() => api.post(`/enquiry/${e.id}/drawings-ready`, { note }))} />
            </ActionCard>
          )}
          {canReviewDrawings && (
            <ActionCard title="Approve 2D/3D drawings" icon={CheckCircle2}>
              <DrawingsApprovalControl e={e} doAction={doAction} />
            </ActionCard>
          )}
          {canCreateQuote && (
            <ActionCard title={e.linked_quote_id ? "Edit quote" : "Create quote"} icon={FileText}>
              <p className="text-xs text-slate-600 mb-3">
                {e.linked_quote_id
                  ? "This enquiry has a linked quote. Open it to finalise pricing after drawings are ready."
                  : "Opens the Quote Builder pre-filled with this enquiry's customer details."}
              </p>
              {e.linked_quote_id ? (
                <Link to={`/quotes/${e.linked_quote_id}/edit`} data-testid="action-edit-quote"
                      className="w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm">
                  Edit quote →
                </Link>
              ) : (
                <Link to={`/quotes/new?enquiry_id=${e.id}`} data-testid="action-create-quote"
                      className="w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm">
                  Start quote →
                </Link>
              )}
            </ActionCard>
          )}
          {canDealerCreateQuote && (
            <ActionCard title="Create quote" icon={FileText}>
              <p className="text-xs text-slate-600 mb-3">
                Skip the sales assignment — build the quote yourself now.
                It&rsquo;ll be listed under your linked Sales rep automatically.
              </p>
              <Link to={`/quotes/new?enquiry_id=${e.id}`}
                    data-testid="action-dealer-create-quote"
                    className="w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm">
                Start quote →
              </Link>
            </ActionCard>
          )}
          {canSendToSales && (
            <ActionCard title="Send quote to Sales for review" icon={Send}>
              <SimpleNoteForm cta="Route to Sales" testId="action-send-to-sales" optional
                onSubmit={(note) => doAction(() => api.post(`/enquiry/${e.id}/send-to-sales`, { note }))} />
            </ActionCard>
          )}
          {canSendToCustomer && (
            <ActionCard title="Send quote to customer" icon={Send}>
              <SimpleNoteForm cta="Send to customer" testId="action-send-to-customer" optional
                onSubmit={(note) => doAction(() => api.post(`/enquiry/${e.id}/send-to-customer`, { note }))} />
            </ActionCard>
          )}
          {canMarkApproved && (
            <ActionCard title="Record customer approval" icon={CheckCircle2}>
              <SimpleNoteForm cta="Customer approved" testId="action-customer-approved" optional
                onSubmit={(note) => doAction(() => api.post(`/enquiry/${e.id}/customer-approved`, { note }))} />
            </ActionCard>
          )}
          {canConvertToProject && (
            <ActionCard title="Convert to project" icon={ArrowRightCircle}>
              <p className="text-xs text-slate-600 mb-3">Customer approved. Convert the linked quote into a project — this snapshots the quote and starts the production pipeline.</p>
              <Link to={`/quotes/${e.linked_quote_id}`} data-testid="action-convert"
                    className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-sm">
                Open quote to convert →
              </Link>
            </ActionCard>
          )}
          {canSetSubStatus && false /* Manual stage status hidden — auto-derived from workflow_status */ && (
            <ActionCard title="Stage status" icon={Pause}>
              <SubStatusForm currentSub={e.sub_status || "in_progress"} currentReason={e.sub_status_reason}
                onSubmit={(sub_status, reason) => doAction(() => api.post(`/enquiry/${e.id}/sub-status`, { sub_status, reason }))} />
            </ActionCard>
          )}
          {canLose && (
            <ActionCard title="Close as Lost" icon={XCircle}>
              <LoseForm onSubmit={(reason) =>
                doAction(() => api.post(`/enquiry/${e.id}/lose`, { reason }))} />
            </ActionCard>
          )}
          {canReopen && (
            <ActionCard title="Reopen" icon={RotateCw}>
              <button data-testid="action-reopen" disabled={busy}
                onClick={() => doAction(() => api.post(`/enquiry/${e.id}/reopen`))}
                className="w-full inline-flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-50 font-bold px-4 py-2 rounded-sm disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reopen enquiry"}
              </button>
            </ActionCard>
          )}
          {e.linked_quote_id && (
            <LinkedQuoteCard quoteId={e.linked_quote_id} />
          )}
        </div>
      </div>
      {showEdit && (
        <EditEnquiryModal e={e} onClose={() => setShowEdit(false)}
          onSaved={(updated) => { setE(updated); setShowEdit(false); toast.success("Enquiry updated"); }} />
      )}
    </DesktopLayout>
  );
}

function EditEnquiryModal({ e, onClose, onSaved }) {
  const [name, setName] = useState(e.name || "");
  const [phone, setPhone] = useState(e.phone || "");
  const [email, setEmail] = useState(e.email || "");
  const [address, setAddress] = useState(e.address || "");
  const [notes, setNotes] = useState(e.notes || "");
  const [timeline, setTimeline] = useState(e.timeline || "");
  const [reqs, setReqs] = useState(new Set(e.requirements || []));
  const [busy, setBusy] = useState(false);
  const REQUIREMENTS = ["Kitchen", "Wardrobes", "Vanity", "Only Carcass", "Others"];
  const TIMELINES = ["Immediate", "1 month", "2-3 months", "3+ months"];
  const toggle = (r) => { const nx = new Set(reqs); nx.has(r) ? nx.delete(r) : nx.add(r); setReqs(nx); };
  const save = async () => {
    if (!name.trim() || !phone.trim()) return toast.error("Name and phone are required");
    setBusy(true);
    try {
      const payload = {
        name: name.trim(), phone: phone.trim(),
        email: email.trim() || null, address: address.trim() || null,
        notes: notes.trim() || null, timeline: timeline || null,
        requirements: Array.from(reqs),
      };
      const { data } = await api.patch(`/enquiry/${e.id}`, payload);
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}
      data-testid="edit-enquiry-modal">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-black tracking-tight" style={{ fontFamily: "Chivo" }}>Edit enquiry</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 text-xl leading-none px-2">×</button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <div className="hk-tiny-label mb-1">Name *</div>
            <input className={FIELD} value={name} onChange={(ev) => setName(ev.target.value)} data-testid="edit-enq-name" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="hk-tiny-label mb-1">Phone *</div>
              <input className={FIELD} value={phone} onChange={(ev) => setPhone(ev.target.value)} data-testid="edit-enq-phone" />
            </label>
            <label className="block">
              <div className="hk-tiny-label mb-1">Email</div>
              <input className={FIELD} value={email} onChange={(ev) => setEmail(ev.target.value)} data-testid="edit-enq-email" />
            </label>
          </div>
          <label className="block">
            <div className="hk-tiny-label mb-1">Site address</div>
            <textarea rows={2} className={FIELD} value={address} onChange={(ev) => setAddress(ev.target.value)} data-testid="edit-enq-address" />
          </label>
          <div>
            <div className="hk-tiny-label mb-1">Requirements</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {REQUIREMENTS.map((r) => (
                <label key={r} className={`flex items-center gap-2 border rounded-sm px-3 py-2 cursor-pointer text-xs ${reqs.has(r) ? "bg-orange-50 border-orange-500 text-orange-900" : "border-slate-300 text-slate-700"}`}>
                  <input type="checkbox" className="w-3.5 h-3.5 accent-orange-500" checked={reqs.has(r)} onChange={() => toggle(r)} />
                  {r}
                </label>
              ))}
            </div>
          </div>
          <label className="block">
            <div className="hk-tiny-label mb-1">Timeline</div>
            <select className={FIELD} value={timeline} onChange={(ev) => setTimeline(ev.target.value)} data-testid="edit-enq-timeline">
              <option value="">— Select —</option>
              {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="hk-tiny-label mb-1">Notes</div>
            <textarea rows={3} className={FIELD} value={notes} onChange={(ev) => setNotes(ev.target.value)} data-testid="edit-enq-notes" />
          </label>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex items-center gap-2">
          <button onClick={onClose} className="text-xs font-black uppercase px-3 py-2 border border-slate-300 rounded-sm text-slate-700 hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="edit-enq-save"
            className="ml-auto text-xs font-black uppercase px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-sm inline-flex items-center gap-1">
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

function DlPair({ label, value }) {
  return (
    <div>
      <dt className="hk-tiny-label">{label}</dt>
      <dd className="font-semibold text-slate-900 mt-0.5">{value}</dd>
    </div>
  );
}

function ActionCard({ title, icon: Icon, children }) {
  return (
    <div className="hk-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-orange-500" />
        <h3 className="font-bold text-sm">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function AssignForm({ options, onSubmit, testId, cta, allowClear = false }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <form onSubmit={async (e) => { e.preventDefault(); setBusy(true); await onSubmit(val); setBusy(false); }}>
      <select className={FIELD} value={val} onChange={(e) => setVal(e.target.value)} data-testid={`${testId}-select`}>
        <option value="">— Select —</option>
        {options.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.email})</option>)}
      </select>
      <div className="flex gap-2 mt-2">
        <button type="submit" disabled={!val || busy} data-testid={testId}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : cta}
        </button>
        {allowClear && (
          <button type="button" disabled={busy} onClick={async () => { setBusy(true); await onSubmit(""); setBusy(false); }}
            data-testid={`${testId}-clear`}
            className="inline-flex items-center justify-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold px-3 py-2 rounded-sm text-xs disabled:opacity-50">
            Clear
          </button>
        )}
      </div>
    </form>
  );
}

function AttendForm({ options, onSubmit }) {
  const [coordinator_id, setCoord] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <form onSubmit={async (e) => { e.preventDefault(); if (!coordinator_id) return; setBusy(true); await onSubmit(coordinator_id, notes); setBusy(false); }}>
      <select className={FIELD} value={coordinator_id} onChange={(e) => setCoord(e.target.value)} data-testid="action-attend-coord">
        <option value="">— Select Sales Coordinator —</option>
        {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
      <textarea className={`${FIELD} mt-2`} rows={2} placeholder="Optional call notes / message to Sales Coord"
        value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="action-attend-notes" />
      <button type="submit" disabled={!coordinator_id || busy} data-testid="action-attend"
        className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send to Sales Coordination →"}
      </button>
    </form>
  );
}

function LoseForm({ onSubmit }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <form onSubmit={async (e) => { e.preventDefault(); if (!reason.trim()) return; setBusy(true); await onSubmit(reason.trim()); setBusy(false); }}>
      <textarea className={FIELD} rows={2} placeholder="Reason (required)" data-testid="action-lose-reason"
        value={reason} onChange={(e) => setReason(e.target.value)} />
      <button type="submit" disabled={!reason.trim() || busy} data-testid="action-lose"
        className="mt-2 w-full inline-flex items-center justify-center gap-2 border border-red-300 text-red-700 hover:bg-red-50 font-bold px-4 py-2 rounded-sm disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Close as Lost"}
      </button>
    </form>
  );
}

function UploadDrop({ enquiryId, onDone }) {
  const [busy, setBusy] = useState(false);
  const [folder, setFolder] = useState("");
  const upload = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setBusy(true);
    try {
      for (const f of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", f);
        if (folder.trim()) fd.append("folder", folder.trim());
        await api.post(`/enquiry/${enquiryId}/attach`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(folder.trim() ? `Uploaded to "${folder.trim()}"` : "Uploaded");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-2">
      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide flex items-center gap-1">
        <Folder className="w-3 h-3" /> Folder <span className="text-slate-400 font-normal normal-case">(optional)</span>
      </label>
      <input type="text" value={folder} onChange={(ev) => setFolder(ev.target.value)}
        placeholder="e.g. Site photos · Floor plan · Handles"
        maxLength={60}
        className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white"
        data-testid={`enq-upload-folder-${enquiryId}`} />
      <label htmlFor={`up-${enquiryId}`} className="flex items-center gap-2 border border-dashed border-slate-300 rounded-sm px-3 py-2.5 cursor-pointer hover:border-orange-400 hover:bg-orange-50/40 text-sm text-slate-600">
        {busy ? <Loader2 className="w-4 h-4 animate-spin text-orange-500" /> : <Upload className="w-4 h-4 text-slate-400" />}
        {busy ? "Uploading…" : "Attach more files"}
        <input id={`up-${enquiryId}`} type="file" multiple hidden accept="image/*,application/pdf,.dwg,.dxf"
          onChange={(e) => upload(e.target.files)} />
      </label>
    </div>
  );
}

// Groups files by folder. Root-level files show as flat list first, then each
// named folder as a collapsible-looking section. Delete button visible when
// canDelete is true (Sales Coord, Prod Coord, Super Admin).
function FileGroupList({ enquiryId, files, canDelete, onDeleted }) {
  const [busyId, setBusyId] = useState("");
  const groups = React.useMemo(() => {
    const out = {};
    for (const f of files || []) {
      const k = (f.folder || "").trim() || "";
      if (!out[k]) out[k] = [];
      out[k].push(f);
    }
    return Object.entries(out).sort(([a], [b]) => {
      if (a === "" && b !== "") return -1;
      if (b === "" && a !== "") return 1;
      return a.localeCompare(b);
    });
  }, [files]);
  const remove = async (fid, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setBusyId(fid);
    try {
      await api.delete(`/enquiry/${enquiryId}/files/${fid}`);
      toast.success("File deleted");
      onDeleted && onDeleted();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(""); }
  };
  return (
    <div className="space-y-3">
      {groups.map(([folder, items]) => (
        <div key={folder || "__root"}>
          {folder && (
            <div className="flex items-center gap-1 text-[10px] uppercase font-bold text-slate-500 mb-1">
              <Folder className="w-3 h-3" /> {folder}
              <span className="text-slate-400 font-normal">({items.length})</span>
            </div>
          )}
          <ul className="space-y-1.5">
            {items.map((f) => (
              <li key={f.id}
                className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-sm gap-2"
                data-testid={`enq-file-${f.id}`}>
                <a href={`${api.defaults.baseURL}/enquiry/${enquiryId}/files/${f.id}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-orange-600 min-w-0 flex-1">
                  <Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span className="truncate">{f.name}</span>
                  <span className="text-xs text-slate-400 shrink-0">{Math.round((f.size || 0) / 1024)} KB</span>
                </a>
                {canDelete && (
                  <button type="button" onClick={() => remove(f.id, f.name)}
                    disabled={busyId === f.id}
                    title="Delete this attachment"
                    className="text-slate-400 hover:text-rose-600 disabled:opacity-50 shrink-0"
                    data-testid={`enq-file-delete-${f.id}`}>
                    {busyId === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function SimpleNoteForm({ onSubmit, cta, testId, optional = false }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <form onSubmit={async (ev) => {
      ev.preventDefault();
      if (!optional && !note.trim()) return;
      setBusy(true); await onSubmit(note.trim()); setBusy(false); setNote("");
    }}>
      <textarea className={FIELD} rows={2}
        placeholder={optional ? "Optional note" : "Note (required)"}
        value={note} onChange={(ev) => setNote(ev.target.value)}
        data-testid={`${testId}-note`} />
      <button type="submit" disabled={(!optional && !note.trim()) || busy} data-testid={testId}
        className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : cta}
      </button>
    </form>
  );
}

function SubStatusForm({ currentSub, currentReason, onSubmit }) {
  const [sub, setSub] = useState(currentSub || "in_progress");
  const [reason, setReason] = useState(currentReason || "");
  const [busy, setBusy] = useState(false);
  const needsReason = BLOCKING_SUB_STATUSES.has(sub);
  const changed = sub !== currentSub || reason !== (currentReason || "");
  return (
    <form onSubmit={async (e) => {
      e.preventDefault();
      if (needsReason && !reason.trim()) return;
      setBusy(true); await onSubmit(sub, reason.trim()); setBusy(false);
    }}>
      <select className={FIELD} value={sub} onChange={(e) => setSub(e.target.value)} data-testid="sub-status-select">
        {SUB_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
      </select>
      {needsReason && (
        <textarea className={`${FIELD} mt-2`} rows={2} placeholder="Reason (required)"
          value={reason} onChange={(e) => setReason(e.target.value)} data-testid="sub-status-reason" />
      )}
      <button type="submit" disabled={busy || !changed || (needsReason && !reason.trim())} data-testid="sub-status-submit"
        className="mt-2 w-full inline-flex items-center justify-center gap-2 border border-slate-300 text-slate-800 hover:bg-slate-50 font-bold px-4 py-2 rounded-sm disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update status"}
      </button>
    </form>
  );
}


/** Item 7 — Once Sales Coord prepares the quote, the enquiry should show
 * the current quote status (number, revision, grand total, whether it has been
 * converted to a project, and how many revisions exist). */
function LinkedQuoteCard({ quoteId }) {
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/quote/quotes/${quoteId}`);
        setQ(data);
      } catch (e) { setErr(formatApiError(e)); }
    })();
  }, [quoteId]);
  return (
    <ActionCard title="Linked quote" icon={FileText}>
      {err && <div className="text-xs text-red-600">{err}</div>}
      {q ? (
        <div className="space-y-2" data-testid="linked-quote-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-mono text-xs font-bold text-slate-800">{q.quote_number}</div>
              <div className="text-[10px] text-slate-500">Rev {q.version || 1}</div>
            </div>
            <QuoteStatusPill q={q} />
          </div>
          <div className="text-sm font-semibold text-slate-900">
            ₹ {((q.totals || {}).grand_total || 0).toLocaleString()}
          </div>
          <div className="text-[11px] text-slate-500">
            {q.lines?.length || 0} module{q.lines?.length === 1 ? "" : "s"}
            {q.extra_items?.length ? ` · ${q.extra_items.length} extras` : ""}
          </div>
          <Link to={`/quotes/${quoteId}`} className="text-orange-600 hover:text-orange-700 font-bold text-sm inline-flex items-center gap-1" data-testid="linked-quote-open">
            Open quote →
          </Link>
        </div>
      ) : !err && <div className="text-xs text-slate-500">Loading quote…</div>}
    </ActionCard>
  );
}

function QuoteStatusPill({ q }) {
  const status = q.converted_to_project_id ? "converted" : q.sent_to_customer_at ? "sent" : "draft";
  const meta = {
    converted: { label: "Converted", color: "#15803d", bg: "#dcfce7" },
    sent:      { label: "Sent to Customer", color: "#7c3aed", bg: "#ede9fe" },
    draft:     { label: "Draft", color: "#c2410c", bg: "#ffedd5" },
  }[status];
  return (
    <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-sm" style={{ color: meta.color, backgroundColor: meta.bg }} data-testid="quote-status-pill">
      {meta.label}
    </span>
  );
}


/** Sales — inline approve / reject control for a submitted 2D/3D drawing.
 * The reject flow requires a reason; the enquiry then bounces back to Design
 * with `drawings_reject_reason` set for context. */
function DrawingsApprovalControl({ e, doAction }) {
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState("");
  const drawings = (e.files || []).filter((f) => (f.uploaded_by_role || "").includes("design"));
  return (
    <div>
      <p className="text-xs text-slate-600 mb-3">
        2D/3D drawings were submitted by <b>{e.drawings_submitted_by || "Design"}</b>
        {e.drawings_submitted_at && <> · {new Date(e.drawings_submitted_at).toLocaleString()}</>}.
        Approve to unlock quote preparation, or reject with a reason to send it back.
      </p>
      {drawings.length > 0 && (
        <ul className="mb-3 space-y-1 max-h-32 overflow-y-auto">
          {drawings.slice(0, 4).map((f) => (
            <li key={f.id} className="text-[11px] text-slate-500 truncate" data-testid={`drawings-file-${f.id}`}>
              📎 {f.name}
            </li>
          ))}
          {drawings.length > 4 && (
            <li className="text-[10px] italic text-slate-400">+{drawings.length - 4} more</li>
          )}
        </ul>
      )}
      {!showReject ? (
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => doAction(() => api.post(`/enquiry/${e.id}/drawings/approve`, {}))}
            data-testid="drawings-approve"
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase text-xs px-3 py-2.5 rounded-sm">
            Approve →
          </button>
          <button onClick={() => setShowReject(true)}
            data-testid="drawings-reject-open"
            className="border border-red-300 text-red-700 hover:bg-red-50 font-black uppercase text-xs px-3 py-2.5 rounded-sm">
            Reject / Rework
          </button>
        </div>
      ) : (
        <div>
          <textarea rows={2} value={reason} onChange={(ev) => setReason(ev.target.value)}
            placeholder="Reason (required) — e.g. Please adjust the tall unit height to 2100"
            className="w-full border border-red-300 rounded-sm px-3 py-2 text-sm bg-white"
            data-testid="drawings-reject-reason" />
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button onClick={() => { setShowReject(false); setReason(""); }}
              className="border border-slate-300 hover:bg-slate-50 text-slate-700 font-black uppercase text-xs px-3 py-2 rounded-sm">
              Cancel
            </button>
            <button onClick={() => doAction(() => api.post(`/enquiry/${e.id}/drawings/reject`, { sub_status: "in_progress", reason }))}
              disabled={!reason.trim()} data-testid="drawings-reject-submit"
              className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-black uppercase text-xs px-3 py-2 rounded-sm">
              Send back to Design
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/** Sales Coord — inline control that opens a modal with notes + file picker
 * for pushing to 2D/3D. Files come from the enquiry's existing uploads so the
 * coordinator picks which references the designer should see. */
function RequestDrawingsControl({ e, doAction }) {
  const [open, setOpen] = useState(false);
  const rounds = e.design_rounds || [];
  const roundNo = rounds.length;
  const status = e.design_status;
  const statusLine = {
    pending: `Round ${roundNo} is with the 2D/3D team — waiting for drawings.`,
    submitted: `Round ${roundNo} submitted${e.drawings_submitted_by ? ` by ${e.drawings_submitted_by}` : ""} — awaiting approval.`,
    approved: `Round ${roundNo} approved${e.drawings_approved_by ? ` by ${e.drawings_approved_by}` : ""}. Push again to start a new round.`,
  }[status];
  return (
    <div>
      {statusLine && (
        <p className="text-[11px] font-bold text-slate-700 bg-slate-100 border-l-2 border-slate-400 px-2 py-1.5 mb-2 rounded-sm"
          data-testid="design-round-status">
          {statusLine}
        </p>
      )}
      <p className="text-xs text-slate-600 mb-3">
        Send a message and select which files (drawings, photos, references) the
        2D/3D team should see.
      </p>
      <button onClick={() => setOpen(true)} data-testid="action-request-drawings"
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black uppercase text-xs px-4 py-2.5 rounded-sm">
        {roundNo > 0 ? "Push again to 2D/3D" : "Open push-to-design form"}
      </button>
      {open && <RequestDrawingsModal e={e} onClose={() => setOpen(false)}
        onSubmit={(payload) => {
          doAction(() => api.post(`/enquiry/${e.id}/request-drawings`, payload));
          setOpen(false);
        }} />}
    </div>
  );
}

function RequestDrawingsModal({ e, onClose, onSubmit }) {
  const [notes, setNotes] = useState("");
  const [selected, setSelected] = useState(new Set(
    (e.design_brief?.attached_file_ids || [])
  ));
  const files = e.files || [];
  const toggle = (fid) => {
    const nx = new Set(selected);
    nx.has(fid) ? nx.delete(fid) : nx.add(fid);
    setSelected(nx);
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
      onClick={onClose} data-testid="request-drawings-modal">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg" onClick={(ev) => ev.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="hk-tiny-label" style={{ color: "#E52B50" }}>Push to 2D/3D</div>
            <h3 className="text-lg font-black tracking-tight" style={{ fontFamily: "Chivo" }}>
              Brief the Design team
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 text-xl leading-none px-2" data-testid="modal-close">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="hk-tiny-label mb-2">Notes for 2D/3D Team</div>
            <textarea rows={4} value={notes} onChange={(ev) => setNotes(ev.target.value)}
              placeholder="e.g. 12ft L-shaped kitchen, island in centre, follow attached floor plan"
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white"
              data-testid="drawings-notes" />
          </div>
          <div>
            <div className="hk-tiny-label mb-2">Attach files for design ({selected.size} selected)</div>
            {files.length === 0 ? (
              <div className="text-xs text-slate-500 italic">No files uploaded on this enquiry yet. Upload from the enquiry page first.</div>
            ) : (
              <ul className="max-h-48 overflow-y-auto border border-slate-200 rounded-sm divide-y divide-slate-200">
                {files.map((f) => (
                  <li key={f.id}>
                    <label className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-slate-50 cursor-pointer" data-testid={`drawings-file-${f.id}`}>
                      <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggle(f.id)} />
                      <span className="flex-1 truncate">{f.name}</span>
                      <span className="text-[9px] font-black uppercase text-slate-400">{(f.size / 1024).toFixed(0)}kb</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[10px] text-slate-400 italic mt-2">
              The designer will ONLY see the customer name, requirements, your notes and these attached files.
              Contact info and address stay private.
            </p>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex items-center gap-2">
          <button onClick={onClose} className="text-xs font-black uppercase px-3 py-2 border border-slate-300 rounded-sm text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={() => onSubmit({ notes, attached_file_ids: Array.from(selected) })}
            className="ml-auto text-xs font-black uppercase px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-sm"
            data-testid="drawings-submit">
            Send to 2D/3D →
          </button>
        </div>
      </div>
    </div>
  );
}

